import { parseString } from "@de-xima/xima-common-js-lang";
import type Framebus from "framebus";
import type { FramebusReplyHandler, FramebusSubscriberArg } from "framebus/dist/lib/types.d.ts";
import jQuery from "jquery";

import type { IFramebusTopMaskPanelOptions, IInsertData, IInsertionOpts, IMaskPanel } from "../../Header.ts";
import { AbstractMaskPanel } from "../AbstractMaskPanel.ts";

/**
 * Mask panel that takes talks with the child mask panel via a framebus. For the
 * top window where the actual mask panel is located.
 *
 * Communication is like this:
 *
 * ```text
 *                   TopMask
 *                           \
 *                            \
 *                             ---- TopPanel ---- DialogPanel
 *                            /
 *                           / via framebus
 *                          /
 * ChildMask ---- ChildPanel
 * ```
 */
export class FramebusTopMaskPanel extends AbstractMaskPanel implements IMaskPanel {
  /**
   * Framebus for communication with the child panel.
   */
  private _framebus: Framebus;

  private _lastTarget: { type: "top" } | { type: "child"; target: string };

  /**
   * Actual mask panel implementing the UI.
   */
  private _maskPanel: IMaskPanel;

  /**
   * @param options Options to configure this instance.
   */
  constructor(options: IFramebusTopMaskPanelOptions) {
    super();
    this._lastTarget = { type: "top" };
    this._maskPanel = options.maskPanel;
    this._framebus = options.framebus;
    this._registerListeners();
  }

  override close(): void {
    this._maskPanel.close();
  }

  override focused(): void {
    this._lastTarget = { type: "top" };
  }

  override async getSelectedValue(): Promise<IInsertData | undefined> {
    return this._maskPanel.getSelectedValue();
  }

  override async isOpen(): Promise<boolean> {
    return this._maskPanel.isOpen();
  }

  override onExternalChange(): void {
    this._maskPanel.onExternalChange();
  }

  override open($position: JQuery): void {
    this._lastTarget = { type: "top" };
    this._maskPanel.open($position);
  }

  override update(): void {
    this._maskPanel.update();
  }

  /**
   * @param data Data received from the sender.
   * @param reply Library handler for sending a reply to the sender.
   */
  private async _onFramebusPlaceholderDialog(data: FramebusSubscriberArg, reply: FramebusReplyHandler): Promise<void> {
    const action = String(data.action);
    switch (action) {
      case "isOpen":
        try {
          const isOpen = await this._maskPanel.isOpen();
          reply(isOpen);
        } catch {
          reply(false);
        }
        break;
      case "getSelectedValue":
        try {
          const selectedValue = await this._maskPanel.getSelectedValue();
          reply(selectedValue);
        } catch {
          reply(undefined);
        }
        break;
      case "onExternalChange":
        this._maskPanel.onExternalChange();
        break;
      case "focused":
        this._lastTarget = { target: parseString(data.target), type: "child" };
        break;
      case "open":
        this._lastTarget = { target: parseString(data.target), type: "child" };
        this._maskPanel.open(jQuery());
        break;
      case "update":
        this._maskPanel.update();
        break;
      case "close":
        this._maskPanel.close();
        break;
    }
  }

  /**
   * @param opts Insertion options received from the sender.
   */
  private _onInsertion(opts: IInsertionOpts): void {
    switch (this._lastTarget.type) {
      case "child":
        this._framebus.emit("placeholderDialogInsert", {
          opts: opts as unknown as Record<string, unknown>,
          target: this._lastTarget.target,
        });
        break;
      case "top":
        this._insertionListener(opts);
        break;
    }
  }

  private _registerListeners() {
    this._maskPanel.setInsertionListener((opts) => this._onInsertion(opts));
    this._framebus.on("placeholderDialog", (data, reply) => {
      this._onFramebusPlaceholderDialog(data, reply).catch((e) =>
        console.error("Could not process placeholder dialog", e),
      );
    });
  }
}
